import {
  animateStreamLainDropdownPanelClose,
  applyStreamLainMenuTheme,
  decorateStreamLainDropdownPanel,
  ensureStreamLainDropdownMenuChromeStyle,
  STREAMLAIN_DROPDOWN_MENU_CLASS,
  STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS,
  STREAMLAIN_DROPDOWN_MENU_OFFSET,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import { popupTargetFromElement } from '@blocksuite/affine/components/context-menu';
import { flip, type Middleware, offset, shift, size } from '@floating-ui/dom';

export type StreamLainDropdownMenuHandler = {
  close: () => void;
  closeWithAnimation?: (afterClose?: () => void) => void;
  menu?: {
    close?: () => void;
    menuElement: HTMLElement;
  };
};

type StreamLainDropdownMenuLayoutOptions = {
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  menuPadding?: string;
  menuGap?: string;
  rowPadding?: string;
  rowGap?: string;
  rowRadius?: string;
};

type StreamLainDropdownMenuOptions = {
  layout?: StreamLainDropdownMenuLayoutOptions;
  onClosed?: () => void;
};

type StreamLainDropdownMenuSizeOptions = {
  boundary?: Element;
  minHeight?: number;
  widthBoundary?: Element;
  padding?: number;
};

type StreamLainDropdownMenuMiddlewareOptions = {
  horizontalBoundary?: Element;
  horizontalPadding?: number;
  padding?: number;
};

export const STREAMLAIN_DROPDOWN_MENU_BOUNDARY_WIDTH_VAR =
  '--streamlain-chat-input-dropdown-boundary-width';

const DEFAULT_LAYOUT: Required<
  Pick<
    StreamLainDropdownMenuLayoutOptions,
    'menuPadding' | 'menuGap' | 'rowPadding' | 'rowGap' | 'rowRadius'
  >
> = {
  menuPadding: '8px',
  menuGap: '4px',
  rowPadding: '8px 12px',
  rowGap: '8px',
  rowRadius: '8px',
};

export function popupTargetFromBodyElement(element: HTMLElement) {
  return {
    ...popupTargetFromElement(element),
    root: document.body,
  };
}

export function streamLainDropdownMenuOffsetMiddleware(): Middleware {
  return offset({
    mainAxis: STREAMLAIN_DROPDOWN_MENU_OFFSET,
    crossAxis: 0,
  });
}

export function streamLainDropdownMenuSizeMiddleware(
  options: StreamLainDropdownMenuSizeOptions = {}
): Middleware {
  return size({
    ...(options.boundary ? { boundary: options.boundary } : {}),
    padding: options.padding ?? 16,
    apply({ availableHeight, availableWidth, elements }) {
      const widthBoundary = options.widthBoundary ?? options.boundary;
      const boundaryWidth = widthBoundary?.getBoundingClientRect().width;
      const constrainedWidth =
        typeof boundaryWidth === 'number' &&
        Number.isFinite(boundaryWidth) &&
        boundaryWidth > 0
          ? boundaryWidth
          : availableWidth;
      elements.floating.style.setProperty(
        STREAMLAIN_DROPDOWN_MENU_BOUNDARY_WIDTH_VAR,
        `${Math.max(0, Math.floor(constrainedWidth))}px`
      );
      elements.floating.style.setProperty(
        '--streamlain-disclosure-popover-scroll-max-height',
        `${Math.max(
          options.minHeight ?? 120,
          Math.floor(availableHeight - 8)
        )}px`
      );
    },
  });
}

export function streamLainDropdownMenuBoundaryWidthCss(
  fallback: string
): string {
  return `var(${STREAMLAIN_DROPDOWN_MENU_BOUNDARY_WIDTH_VAR}, ${fallback})`;
}

function streamLainDropdownHorizontalBoundaryMiddleware(
  options: StreamLainDropdownMenuMiddlewareOptions
): Middleware | null {
  const boundary = options.horizontalBoundary;
  if (!boundary) return null;

  return {
    name: 'streamlainDropdownHorizontalBoundary',
    fn({ x, rects }) {
      const boundaryRect = boundary.getBoundingClientRect();
      const padding = options.horizontalPadding ?? 0;
      const minX = boundaryRect.left + padding;
      const maxX = boundaryRect.right - padding - rects.floating.width;
      const nextX = Math.max(minX, Math.min(x, Math.max(minX, maxX)));
      return { x: nextX };
    },
  };
}

export function streamLainDropdownMenuMiddleware(): Array<
  Middleware | null | undefined | false
>;
export function streamLainDropdownMenuMiddleware(
  options?: StreamLainDropdownMenuMiddlewareOptions
): Array<Middleware | null | undefined | false>;
export function streamLainDropdownMenuMiddleware(
  options: StreamLainDropdownMenuMiddlewareOptions = {}
): Array<Middleware | null | undefined | false> {
  return [
    streamLainDropdownMenuOffsetMiddleware(),
    flip(),
    streamLainDropdownMenuSizeMiddleware({
      padding: options.padding,
      widthBoundary: options.horizontalBoundary,
    }),
    shift({ crossAxis: true }),
    streamLainDropdownHorizontalBoundaryMiddleware(options),
  ];
}

export function normalizeStreamLainMenuLayout(
  menuElement: HTMLElement,
  options: StreamLainDropdownMenuLayoutOptions = {}
) {
  const layout = { ...DEFAULT_LAYOUT, ...options };
  const apply = () => {
    applyStreamLainMenuTheme(menuElement);
    menuElement.style.boxSizing = 'border-box';
    menuElement.style.overflow = 'visible';
    menuElement.style.padding = layout.menuPadding;
    menuElement.style.gap = layout.menuGap;
    if (layout.width) menuElement.style.width = layout.width;
    if (layout.minWidth) menuElement.style.minWidth = layout.minWidth;
    if (layout.maxWidth) menuElement.style.maxWidth = layout.maxWidth;

    const body = menuElement.querySelector<HTMLElement>('.affine-menu-body');
    if (body) {
      body.style.boxSizing = 'border-box';
      body.style.width = '100%';
      body.style.gap = layout.menuGap;
      body.style.overflowX = 'hidden';
    }

    menuElement
      .querySelectorAll<HTMLElement>('.affine-menu-body > div')
      .forEach(group => {
        group.style.boxSizing = 'border-box';
        group.style.width = '100%';
      });

    menuElement
      .querySelectorAll<HTMLElement>('affine-menu-button')
      .forEach(button => {
        button.style.display = 'block';
        button.style.width = '100%';
        button.style.boxSizing = 'border-box';

        const row = button.querySelector<HTMLElement>('.affine-menu-button');
        if (row) {
          row.style.boxSizing = 'border-box';
          row.style.width = '100%';
          row.style.padding = layout.rowPadding;
          row.style.borderRadius = layout.rowRadius;
          row.style.gap = layout.rowGap;
        }

        const text = button.querySelector<HTMLElement>(
          '.affine-menu-action-text'
        );
        if (text) {
          text.style.display = 'block';
          text.style.minWidth = '0';
          text.style.overflow = 'hidden';
        }
      });
  };

  apply();
  queueMicrotask(apply);
  requestAnimationFrame(apply);
  const updateComplete = (
    menuElement as HTMLElement & { updateComplete?: Promise<unknown> }
  ).updateComplete;
  if (updateComplete) {
    void updateComplete.then(apply);
  }
  void customElements.whenDefined('affine-menu-button').then(() => {
    requestAnimationFrame(apply);
  });
}

export function configureStreamLainDropdownMenu(
  handler: StreamLainDropdownMenuHandler | null | undefined,
  triggerElement: HTMLElement,
  options: StreamLainDropdownMenuOptions = {}
) {
  const menuElement = handler?.menu?.menuElement;
  const menu = handler?.menu;
  if (!handler || !menuElement || !menu?.close) return;

  ensureStreamLainDropdownMenuChromeStyle();
  normalizeStreamLainMenuLayout(menuElement, options.layout);
  decorateStreamLainDropdownPanel(menuElement, triggerElement);
  installStreamLainDropdownMenuExitAnimation(handler, options.onClosed);
}

function installStreamLainDropdownMenuExitAnimation(
  handler: StreamLainDropdownMenuHandler,
  onClosed?: () => void
) {
  const menuElement = handler.menu?.menuElement;
  const menu = handler.menu;
  if (!menuElement || !menu?.close) return;

  const originalMenuClose = menu.close.bind(menu);
  const closeCallbacks: Array<() => void> = [];
  let closing = false;
  let closed = false;

  const cleanup = () => {
    menuElement.dispatchEvent(new Event('streamlain-dropdown-menu-cleanup'));
  };

  const runCloseCallbacks = () => {
    const callbacks = closeCallbacks.splice(0);
    callbacks.forEach(callback => callback());
  };

  const finishClose = () => {
    if (closed) return;
    closed = true;
    cleanup();
    menuElement.classList.remove(STREAMLAIN_DROPDOWN_MENU_CLASS);
    menuElement.classList.remove(STREAMLAIN_DROPDOWN_MENU_CLOSING_CLASS);
    originalMenuClose();
    onClosed?.();
    runCloseCallbacks();
  };

  const animateClose = (afterClose?: () => void) => {
    if (closed) {
      afterClose?.();
      return;
    }
    if (afterClose) closeCallbacks.push(afterClose);
    if (closing) return;
    closing = true;

    if (
      !menuElement.isConnected ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      finishClose();
      return;
    }

    animateStreamLainDropdownPanelClose(menuElement, finishClose);
  };

  handler.closeWithAnimation = animateClose;
  handler.close = () => animateClose();
  menu.close = () => animateClose();

  const modalElement = menuElement.parentElement;
  if (modalElement) {
    const closeFromModal = (event: MouseEvent | PointerEvent) => {
      if (event.target !== modalElement) return;
      event.preventDefault();
      event.stopPropagation();
      animateClose();
    };
    modalElement.onpointerdown = closeFromModal;
    modalElement.onmousedown = closeFromModal;
    modalElement.oncontextmenu = closeFromModal;
  }
}
